<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { PendoFeature } from '~/models/Pendo'
import Button from '~/components/ui/Button.vue'

const {
  modelValue,
  total,
  pageSize = 20,
  siblingCount = 1
} = defineProps<{
  modelValue: number
  total: number
  pageSize?: number
  siblingCount?: number
}>()

const emit = defineEmits<{
  'update:modelValue': [value: number]
}>()

const totalPages = computed(() => {
  if (!total || total <= 0) {
    return 1
  }
  return Math.max(1, Math.ceil(total / pageSize))
})

const currentPage = computed<number>({
  get() {
    const page = Number(modelValue) || 1
    if (page < 1) {
      return 1
    }
    if (page > totalPages.value) {
      return totalPages.value
    }
    return page
  },
  set(value: number) {
    const clamped = Math.min(Math.max(1, value), totalPages.value)
    emit('update:modelValue', clamped)
  },
})

function goToPage(page: number) {
  currentPage.value = page
}

function range(start: number, end: number): number[] {
  const out: number[] = []
  for (let i = start; i <= end; i++) {
    out.push(i)
  }
  return out
}

const paginationRange = computed<(number | 'dots')[]>(() => {
  const totalPageCount = totalPages.value
  const siblings = Math.max(0, siblingCount)
  const totalNumbersToShow = 5 + siblings * 2

  if (totalNumbersToShow >= totalPageCount) {
    return range(1, totalPageCount)
  }

  const leftSiblingIndex = Math.max(currentPage.value - siblings, 1)
  const rightSiblingIndex = Math.min(currentPage.value + siblings, totalPageCount)

  const showLeftDots = leftSiblingIndex > 2
  const showRightDots = rightSiblingIndex < totalPageCount - 1

  if (!showLeftDots && showRightDots) {
    const leftRange = range(1, 3 + siblings * 2)
    return [...leftRange, 'dots', totalPageCount]
  }

  if (showLeftDots && !showRightDots) {
    const rightRange = range(totalPageCount - (2 + siblings * 2), totalPageCount)
    return [1, 'dots', ...rightRange]
  }

  return [1, 'dots', ...range(leftSiblingIndex, rightSiblingIndex), 'dots', totalPageCount]
})

const { t } = useI18n()
const ELLIPSIS = '…'
</script>

<template>
  <nav
    class="flex items-center gap-2"
    role="navigation"
    :aria-label="t('reports.pagination.title')"
  >
    <Button
      variant="outline"
      shade="dark"
      color="green"
      size="md"
      :disabled="currentPage === 1"
      :aria-label="t('reports.pagination.previous')"
      :data-pendo="PendoFeature.ActivityReports.PaginatePrevious"
      @click="goToPage(currentPage - 1)"
    >
      {{ t('reports.pagination.previous') }}
    </Button>

    <template v-for="(item, idx) in paginationRange" :key="idx">
      <Button
        v-if="item !== 'dots'"
        :variant="item === currentPage ? 'solid' : 'outline'"
        shade="dark"
        color="green"
        size="md"
        :aria-current="item === currentPage ? 'page' : undefined"
        :aria-label="`${t('reports.pagination.page')} ${item}`"
        :data-pendo="PendoFeature.ActivityReports.PaginateNumber"
        @click="goToPage(item as number)"
      >
        {{ item as number }}
      </Button>
      <span
        v-else
        class="text-gray-60 px-1 select-none"
        aria-hidden="true"
        v-text="ELLIPSIS"
      />
    </template>

    <Button
      variant="outline"
      shade="dark"
      color="green"
      size="md"
      :disabled="currentPage === totalPages"
      :aria-label="t('reports.pagination.next')"
      :data-pendo="PendoFeature.ActivityReports.PaginateNext"
      @click="goToPage(currentPage + 1)"
    >
      {{ t('reports.pagination.next') }}
    </Button>
  </nav>
</template>
